// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

interface MetricBottomSummary {
  title: string;
  description: string;
  icon: string;
  iconColor: string;
  borderColor: string;
}

export type ChartType = 'bar'| 'stacked-bar' | 'line' | 'area' | 'no-data' | 'loading';

/**
 * Valid score points values
 */
export type ScorePoints = 0 | 1 | 2 | 3;

/**
 * Valid score level values
 */
export type ScoreLevel = 'Silent' | 'Participating' | 'Contributing' | 'Leading';

/**
 * Score points to level mapping
 */
export const SCORE_MAPPING: Record<ScorePoints, ScoreLevel> = {
  0: 'Silent',
  1: 'Participating',
  2: 'Contributing',
  3: 'Leading'
} as const;

/**
 * Style configuration interface for score levels
 */
export interface ScoreLevelStyles {
  colorName: 'red' | 'yellow' | 'blue' | 'green' | 'gray';
  icon: string;
  iconColor: string;
  borderColor: string;
  signalTagColor: string;
}

/**
 * Level to style mapping for consistent styling across all metrics
 */
export const LEVEL_STYLE_MAPPING: Record<ScoreLevel, ScoreLevelStyles> = {
  'Silent': {
    colorName: 'red',
    icon: 'fas fa-exclamation-circle',
    iconColor: 'var(--lf-color-danger-red)',
    borderColor: 'var(--lf-color-danger-red-light)',
    signalTagColor: 'var(--lf-color-danger-red)'
  },
  'Participating': {
    colorName: 'yellow',
    icon: 'fas fa-minus-circle',
    iconColor: 'var(--lf-color-warning-yellow)',
    borderColor: 'var(--lf-color-warning-yellow-light)',
    signalTagColor: 'var(--lf-color-warning-yellow)'
  },
  'Contributing': {
    colorName: 'blue',
    icon: 'fas fa-thumbs-up',
    iconColor: 'var(--lf-color-info-blue)',
    borderColor: 'var(--lf-color-info-blue-light)',
    signalTagColor: 'var(--lf-color-info-blue)'
  },
  'Leading': {
    colorName: 'green',
    icon: 'fas fa-thumbs-up',
    iconColor: 'var(--lf-color-success-emerald)',
    borderColor: 'var(--lf-color-success-emerald-light)',
    signalTagColor: 'var(--lf-color-success-emerald)'
  }
} as const;

/**
 * Points to style mapping for direct access by score points
 */
export const POINTS_STYLE_MAPPING: Record<ScorePoints, ScoreLevelStyles> = {
  0: LEVEL_STYLE_MAPPING['Silent'],
  1: LEVEL_STYLE_MAPPING['Participating'],
  2: LEVEL_STYLE_MAPPING['Contributing'],
  3: LEVEL_STYLE_MAPPING['Leading']
} as const;


/**
 * Tooltip configuration for metric display
 */
export interface MetricTooltip {
  definition: string;
  scoringRules: string[];
}

/**
 * Base interface for metric configuration
 */
export interface BaseMetricConfig {
  metricType: string;
  display: {
    displayName: string;
    chartType: ChartType;
    tooltip: MetricTooltip;
  };
  bottomSummary: {
    zeroValue: {
      description: string;
    };
    positiveValue: {
      descriptionTemplate: string;
    };
    nullValue?: {
      description: string;
    };
    lowValue?: {
      description: string;
    };
  };
}

/**
 * Base class for metric entities
 *
 * Provides common functionality for all metric entities to reduce code duplication
 * and ensure consistent behavior across different metric types.
 */
export class BaseMetricEntity {

  /**
   * Standard null value description for ecosystem metrics
   * Used when LFX doesn't have data for a specific activity type
   */
  public static readonly NULL_VALUE_DESCRIPTION = 'This project chooses to use non-LFX tools for this activity type, so we don\'t have data for who is involved.';

  /**
   * Standard Non-LF project description for ecosystem metrics
   * Used when the project is not part of the Linux Foundation
   */
  public static readonly NON_LF_PROJECT_DESCRIPTION = 'This project is not part of the LF, so we don\'t have data of this type.';

  /**
   * Generate HTML for scoring rules display
   */
  public static generateRulesHtml(tooltip: MetricTooltip): string {
    const { definition, scoringRules } = tooltip;
    const rulesHtml = scoringRules
      .map(rule => `<li>${rule}</li>`)
      .join('');

    return `
    <ul>
      <li><b>Definition</b>: ${definition}</li>
      <li><b>Scoring</b>:</li>
      <ul>
        ${rulesHtml}
      </ul>
    </ul>
    `;
  }

  /**
   * Format description template with values
   */
  protected static formatDescription(template: string, values: Record<string, string>): string {
    return Object.entries(values).reduce(
      (result, [key, value]) => result.replace(`{${key}}`, value),
      template
    );
  }

  /**
   * Get styles for a specific score level
   */
  public static getStylesByLevel(level: ScoreLevel): ScoreLevelStyles {
    return LEVEL_STYLE_MAPPING[level];
  }

  /**
   * Get styles for a specific score points value
   */
  public static getStylesByPoints(points: ScorePoints): ScoreLevelStyles {
    return POINTS_STYLE_MAPPING[points];
  }

  /**
   * Get null value styles for when no data is available
   */
  public static getNullValueStyles(): ScoreLevelStyles {
    return {
      colorName: 'gray',
      icon: 'fas fa-question-circle',
      iconColor: 'var(--lf-color-neutral-gray)',
      borderColor: 'var(--lf-color-neutral-gray-light)',
      signalTagColor: 'var(--lf-color-neutral-gray-dark)'
    };
  }

  /**
   * Get bottom summary for null values (no data available)
   * Only works for entities that have nullValue configured
   */
  protected static getNullValueBottomSummary(config: BaseMetricConfig): MetricBottomSummary {
    const styles = this.getNullValueStyles();

    // Check if nullValue is configured for this entity
    if (!config.bottomSummary.nullValue) {
      throw new Error(`Entity ${config.metricType} does not support null values. Add nullValue to bottomSummary configuration.`);
    }

    return {
      title: config.display.displayName,
      description: config.bottomSummary.nullValue.description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }

  /**
   * Get bottom summary for Non-LF projects
   * Returns a gray-styled summary with the Non-LF project message
   */
  protected static getNonLfProjectBottomSummary(config: BaseMetricConfig): MetricBottomSummary {
    const styles = this.getNullValueStyles();

    return {
      title: config.display.displayName,
      description: this.NON_LF_PROJECT_DESCRIPTION,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor,
    };
  }

  public static getIconTypeForChartData(chartData: {metricCount: number; othersCount: number}[]): 'eyes' | undefined {
    const allZeros = chartData.every(d =>
      (d.metricCount === 0 || d.metricCount === null) &&
      (d.othersCount === 0 || d.othersCount === null)
    );

    return allZeros ? 'eyes' : undefined;
  }
}
